import * as THREE from 'three';
import { G, M, V, glowSprite } from '../prims.ts';

/*
 * Hero tiers: the miniature grows in four visible steps with the hero's level (I 1–3, II 4–6, III 7–9, IV 10–11).
 * Each tier is a separate baked variant of the same model: the extra pieces reuse the model's own finishes
 * (they fold into the parts it already draws), so a higher tier costs no extra draw calls; tier IV adds glow
 * billboards (one shared instanced draw for the whole board) and at most one self-lit rune material.
 */

export type HeroTier = 1 | 2 | 3 | 4;

/** Tier of a hero by level. */
export const heroTier = (level: number | undefined): HeroTier => {
  const l = level ?? 1;
  return l >= 10 ? 4 : l >= 7 ? 3 : l >= 4 ? 2 : 1;
};

/** Board scale of a tier: the stronger hero stands a little taller among the others. */
export const TIER_SCALE: Record<HeroTier, number> = { 1: 1, 2: 1.03, 3: 1.06, 4: 1.1 };

/** A fuller cape at each tier (length, bottom radius, arc). */
export const tierCape = (t: HeroTier): { capeLen: number; capeR: number; capeArc: number } =>
  [
    { capeLen: 1.2, capeR: 0.42, capeArc: 1.9 },
    { capeLen: 1.28, capeR: 0.45, capeArc: 2.0 },
    { capeLen: 1.36, capeR: 0.48, capeArc: 2.15 },
    { capeLen: 1.42, capeR: 0.52, capeArc: 2.3 },
  ][t - 1]!;

const TAU = Math.PI * 2;

/**
 * Plate pauldron over shoulder `k` (-1 left, 1 right), on the body frame: a dome and `lames` hanging plates,
 * with an optional rim ring in an accent metal.
 */
export const pauldron = (b: THREE.Object3D, k: number, mat: THREE.Material, s = 1, lames = 1, trim?: THREE.Material): THREE.Group => {
  const pa = G(b, k * 0.25, 1.43, 0);
  pa.rotation.z = -k * 0.62;
  pa.scale.setScalar(s);
  const dome = M(new THREE.SphereGeometry(0.105, 10, 4, 0, TAU, 0, Math.PI / 2), mat, pa);
  dome.scale.set(1.05, 0.62, 1.15);
  for (let i = 0; i < lames; i++) {
    const lame = M(new THREE.SphereGeometry(0.1, 10, 2, 0, TAU, Math.PI / 2 - 0.5, 0.5), mat, pa);
    lame.position.y = -0.045 - i * 0.04;
    lame.scale.set(1.12 + i * 0.06, 0.9, 1.2 + i * 0.06);
  }
  if (trim) {
    const r = M(new THREE.TorusGeometry(0.11, 0.011, 3, 16), trim, pa);
    r.rotation.x = Math.PI / 2;
    r.scale.set(1.05, 1.15, 1);
  }
  return pa;
};

/** The soft tier-IV aura: a faint bloom of the class colour around the body and one at the feet. */
export const aura = (parent: THREE.Object3D, color: string): void => {
  glowSprite(parent, color, 2.0, V(0, 1.05, -0.05), 0.13);
  glowSprite(parent, color, 1.3, V(0, 0.12, 0), 0.16);
};

/** A row of small glyph plates along a segment (blade or bow runes). */
export const runeRow = (parent: THREE.Object3D, mat: THREE.Material, from: THREE.Vector3, to: THREE.Vector3, n: number, w = 0.022, h = 0.035, d = 0.016): void => {
  for (let i = 0; i < n; i++) {
    const p = from.clone().lerp(to, n === 1 ? 0.5 : i / (n - 1));
    M(new THREE.BoxGeometry(w, h, d), mat, parent).position.copy(p);
  }
};

/** A flat ring arc of `arc` radians centered on the back (-Z) at height y: the rim of a back-only mantle or collar. */
export const backArc = (parent: THREE.Object3D, r: number, tube: number, mat: THREE.Material, y: number, arc: number): THREE.Mesh => {
  const g = G(parent, 0, y, 0);
  g.rotation.y = arc / 2 - (3 * Math.PI) / 2;
  const m = M(new THREE.TorusGeometry(r, tube, 3, 20, arc), mat, g);
  m.rotation.x = Math.PI / 2;
  return m;
};
